"use client";

import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
//import { getOptions, useRerenderListener } from '@/lib/globals';
//import { useCourses } from './CoursesContext';
import { useCourses } from '@/lib/courses';

import Delta from 'quill-delta';
//import { SUPABASE_URL } from '@/lib/constants';
//import { openRestrictedDialog } from '@/components/RestrictedButton';
//import { createNote, deleteNote, updateNoteCountCache } from '@/lib/accountSync/noteSync';
import { useCommunication } from '@/lib/CommunicationWrapper';
import { createNote } from '../lib/sync';
import { tableNameMap, NoteCountCache } from '../lib/types';
import { useAuth } from '@/lib/auth';
import { CLOUDFLARE_URL } from '@/lib/config';

const NotesContext = createContext<any>(null);

export const useNotes = () => {
    const context = useContext(NotesContext);
    if (!context) {
        throw new Error('useNotes must be used within an NotesProvider');
    }
    return context;
};

export const NotesProvider = ({ children }: { children: React.ReactNode }) => {
    const { settings, authenticatedClient, getOptions, updateOptions, authenticatedFetch } = useCommunication();
    const { planType } = useAuth();
    const { courseData, visibleCourseIds } = useCourses();

    const [activeCourse, setActiveCourse] = useState<string | null>(() => {
        const options = getOptions();
        if (options?.notes_last_active_course) {
            return options.notes_last_active_course;
        } else if (visibleCourseIds.length > 0) {
            return visibleCourseIds[0];
        }
        return null;
    });
    const [activeTab, setActiveTab] = useState<string>("notes");
    const [transcript, setTranscript] = useState<any[]>([]);
    const [liveTranscript, setLiveTranscript] = useState<string>('');
    const [messages, setMessages] = useState<any[]>([]);
    const [notesMetadata, setNotesMetadata] = useState<any>({});
    const [noteId, setNoteId] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [smartNote, setSmartNote] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [noteCounts, setNoteCounts] = useState<Record<string, number>>({});

    const [maxLimitBytes, setMaxLimitBytes] = useState<number>(0);
    const [currentUsageBytes, setCurrentUsageBytes] = useState<number>(0);

    const [canCreateNote, setCanCreateNote] = useState<boolean>(true);
    const [transcriptTabOpen, setTranscriptTabOpen] = useState<boolean>(false);
    const [showRecordingDialog, setShowRecordingDialog] = useState<boolean>(false);

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const waveformHistoryRef = useRef<number[]>([]);
    const maxHistoryLength = useRef<number>(600); // Number of data points to keep (about 10 seconds at 60fps)

    const content = useRef<any>({});
    const smartNoteCache = useRef<any>({});
    const [quillEditor, setQuillEditor] = useState<any>(null);
    const contentInit = useRef<boolean>(false);



    const checkNoteCount = async () => {
        if (planType !== "free" && planType !== "style") return;

        const response = await authenticatedFetch(`${CLOUDFLARE_URL}/notes/check-storage-usage`, {});
        const { message, error } = await response.json();

        if (error) {
            console.error('Error checking note count:', error);
            return;
        }

        const { current_usage_bytes, max_limit_bytes } = message;

        setCurrentUsageBytes(current_usage_bytes || 0);
        setMaxLimitBytes(max_limit_bytes || 0);
        //setCanCreateNote(remaining_mb || 0);
    };

    // Update the local cache immediately when notes are created/deleted
    const updateNoteCountCache = async (course_id: string, action: "insert" | "delete") => {
        const storageKey = tableNameMap["note_counts"];

        // Get data from extension storage via communication layer
        const local = getOptions("local");
        const cachedData: NoteCountCache = local[storageKey] ?? {};

        const currentCount = cachedData[course_id] || 0;
        if (action === "insert") {
            cachedData[course_id] = currentCount + 1;
        } else if (action === "delete") {
            cachedData[course_id] = currentCount - 1;
        }

        // Save back to extension storage via communication layer
        updateOptions({ [storageKey]: cachedData }, "local");
    };

    useEffect(() => {
        //setSettings(loadSettings(settingsKeys));
        checkNoteCount();
        fetchNoteCounts(); // Fetch note counts on mount
    }, [planType]);

    // Fetch real-time note counts from database
    const fetchNoteCounts = async () => {
        const client = await authenticatedClient();
        if (!client) {
            console.error("Error creating auth client:");
            return;
        }

        // Query to get count of notes per course
        const { data, error } = await client
            .from('notes')
            .select('course_id');

        if (error) {
            console.error('Error fetching note counts:', error);
            return;
        }

        // Count notes per course
        const counts: Record<string, number> = {};
        data?.forEach((note: any) => {
            const courseId = note.course_id.toString();
            counts[courseId] = (counts[courseId] || 0) + 1;
        });

        setNoteCounts(counts);
    };

    const getNotesMetadata = async (loadMore = false, limit = 6) => {

        const client = await authenticatedClient();
        if (!client) {
            console.error("Error creating auth client:");
            return;
        }

        if (!activeCourse) return; // no course selected yet

        // If not loading more and already loaded, skip
        if (!loadMore && notesMetadata[activeCourse]) return;

        const currentCount = notesMetadata[activeCourse]?.length || 0;

        let query = client.from('notes').select('id, title, modified_at');

        // Only filter by course_id if there is an active course selected
        if (activeCourse !== "all") {
            query = query.eq('course_id', activeCourse);
        }

        // Add pagination
        const { data, error } = await query
            .order('modified_at', { ascending: false })
            .range(currentCount, currentCount + limit - 1);

        if (error) {
            console.error('Error fetching notes metadata:', error);
            return;
        }

        // If loading more, append to existing data; otherwise replace
        if (loadMore && notesMetadata[activeCourse]) {
            setNotesMetadata((prev: any) => ({
                ...prev,
                [activeCourse]: [...prev[activeCourse], ...(data || [])]
            }));
        } else {
            setNotesMetadata((prev: any) => ({ ...prev, [activeCourse]: data }));
        }
    }

    const getNoteContent = async () => {
        ;
        if (!noteId) return;


        if (content.current[noteId] && quillEditor) {
            quillEditor.setContents(content.current[noteId]);
            return;
        }

        const client = await authenticatedClient();

        if (!client) {
            console.error("Error creating auth client:");
            return;
        }

        const { data, error } = await client
            .from('notes')
            .select('content')
            .eq('id', noteId);

        if (error) {
            console.error('Error fetching note content:', error);
            return null;
        }

        if (data) {
            quillEditor?.setContents(data[0].content);
            content.current[noteId] = new Delta(data[0].content);
        }
    }

    const getSmartNoteContent = async (noteId: string) => {
        if (!noteId) return;
        if (smartNoteCache.current[noteId]) return smartNoteCache.current[noteId];

        const client = await authenticatedClient();

        if (!client) {
            console.error("Error creating auth client:");
            return;
        }

        const { data, error: fetchError } = await client.from('notes').select('smart_note').eq('id', noteId);

        if (fetchError) {
            console.error('Error fetching smart note:', fetchError);
            return;
        }

        smartNoteCache.current[noteId] = data[0].smart_note;
        return data[0].smart_note;
    }

    const handleNewNote = async (immediateTranscribe = false) => {
        if (!activeCourse) return;

        // Check if user can create notes
        if (!canCreateNote) {
            // TODO: implemenet this
            //openRestrictedDialog('notesLimit', { noteCount, noteLimit });
            return;
        }

        const response = await authenticatedFetch(`${CLOUDFLARE_URL}/notes/create-note`, {
            method: "POST",
            body: {
                "course_id": activeCourse,
            },
        });

        const { message, error } = await response.json();

        if (error) {
            console.error('Error creating note');
            return null;
        }

        await updateNoteCountCache(activeCourse, "insert");

        // Update note counts immediately
        setNoteCounts((prev) => ({
            ...prev,
            [activeCourse]: (prev[activeCourse] || 0) + 1
        }));

        console.log('Created note:', message);

        setNotesMetadata((prev: any) => ({ ...prev, [activeCourse]: [...(prev[activeCourse] || []), message] }));
        switchNote(message?.id ?? null);

        // If immediateTranscribe is true, show the recording dialog
        if (immediateTranscribe) {
            setShowRecordingDialog(true);
        }
    }

    const switchTab = (tab: string) => {
        contentInit.current = false;
        setActiveTab(tab);
    }

    //
    // Metadata updates
    //  

    const metadataUpdateTimeout = useRef<any>(null);

    const updateMetadata = async (updates: any) => {
        const client = await authenticatedClient();

        if (!client) {
            console.error("Error creating auth client:");
            return;
        }

        const { error } = await client
            .from('notes')
            .update(updates)
            .eq('id', noteId);

        if (error) {
            console.error('Error updating note title:', error);
            return;
        }

    };

    const handleMetadataChange = (updates: any) => {

        console.log('handleMetadataChange', updates);

        // TODO: show some error message here because this should never happen
        if (!activeCourse) return;
        if (!noteId) return;

        // Clear any existing timeout
        if (metadataUpdateTimeout.current) {
            clearTimeout(metadataUpdateTimeout.current);
        }

        // Set a new timeout
        metadataUpdateTimeout.current = setTimeout(() => {
            updateMetadata(updates);
        }, 3000);

        setNotesMetadata((prev: any) => ({ ...prev, [activeCourse]: prev[activeCourse].map((note: any) => note.id === noteId ? { ...note, ...updates } : note) }));
    }

    const handleDeleteNote = async () => {
        if (!noteId) return;
        if (!activeCourse) return;

        try {
            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/notes/${noteId}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                console.error('Error deleting note');
                return;
            }

            await updateNoteCountCache(activeCourse, "delete");
            
            // Update note counts immediately
            setNoteCounts((prev) => ({
                ...prev,
                [activeCourse]: Math.max((prev[activeCourse] || 0) - 1, 0)
            }));
            
            setNotesMetadata((prev: any) => ({ ...prev, [activeCourse]: prev[activeCourse].filter((note: any) => note.id !== noteId) }));
            setNoteId(null);
            setSidebarOpen(false);
        } catch (error) {
            console.error('Error deleting note:', error);
        }
    }

    const deleteNoteById = async (targetNoteId: string) => {
        if (!targetNoteId) return;
        if (!activeCourse) return;

        try {
            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/notes/${targetNoteId}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                console.error('Error deleting note');
                return;
            }

            await updateNoteCountCache(activeCourse, "delete");
            
            // Update note counts immediately
            setNoteCounts((prev) => ({
                ...prev,
                [activeCourse]: Math.max((prev[activeCourse] || 0) - 1, 0)
            }));
            
            setNotesMetadata((prev: any) => ({ ...prev, [activeCourse]: prev[activeCourse].filter((note: any) => note.id !== targetNoteId) }));
            if (noteId === targetNoteId) {
                setNoteId(null);
                setSidebarOpen(false);
            }
        } catch (error) {
            console.error('Error deleting note:', error);
        }
    }


    useEffect(() => {
        setNoteId(null);
        getNotesMetadata();
    }, [activeCourse]);

    const updateTranscript = (updates: any) => {
        setTranscript((prevTranscript: any) => [...prevTranscript, ...updates]);
    }

    const switchNote = (noteId: string) => {
        contentInit.current = false;
        setNoteId(noteId);
        setMessages([]);
        setTranscript([]);
        setLiveTranscript('');
        setActiveTab("notes");
    }

    const handleAsk = async (question: string) => {
        /*
    
        // Add the user's message immediately
        setMessages(prev => [...prev, { role: 'user', content: question }]);
    
        try {
            const res = await fetch(`${SUPABASE_URL}/functions/v1/note-chat`, {
                method: "POST",
                body: JSON.stringify({
                    question: question,
                    noteId: noteId,
                    context: ["notes", "transcript"]
                }),
                headers: {
                    'Authorization': `Bearer ${settings.access_token}`,
                    'Content-Type': 'application/json'
                }
            });
    
            if (!res.ok) {
                throw new Error('Failed to get response');
            }
    
            if (!res.body) {
                throw new Error('No response body');
            }
    
            // Create a new message for the assistant's response
            setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
    
            // Read the response stream
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedResponse = '';
    
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
    
                // Decode the chunk and split by double newlines (SSE format)
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n\n');
    
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const eventData = JSON.parse(line.slice(6));
    
                            if (eventData.type === 'response.output_text.delta') {
                                accumulatedResponse += eventData.delta || '';
                                // Update the last message with the accumulated response
                                setMessages(prev => {
                                    const newMessages = [...prev];
                                    newMessages[newMessages.length - 1].content = accumulatedResponse;
                                    return newMessages;
                                });
                            } else if (eventData.type === 'error') {
                                console.error('Stream error:', eventData.error);
                                throw new Error(eventData.error);
                            }
                        } catch (e) {
                            console.error('Error parsing stream data:', e);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error while processing your request.'
            }]);
        }
            */
    };

    const isWidget = false;

    const value: any = {
        audioContextRef,
        analyserRef,
        animationFrameRef,
        waveformHistoryRef,
        maxHistoryLength,
        transcript,
        setTranscript,
        liveTranscript,
        setLiveTranscript,
        activeCourse,
        setActiveCourse,
        activeTab,
        setActiveTab,
        quillEditor,
        setQuillEditor,
        updateTranscript,
        messages,
        setMessages,
        handleAsk,
        content,
        switchNote,
        notesMetadata,
        setNotesMetadata,
        noteId,
        setNoteId,
        handleNewNote,
        handleMetadataChange,
        sidebarOpen,
        setSidebarOpen,
        handleDeleteNote,
        deleteNoteById,
        isRecording,
        setIsRecording,
        contentInit,
        switchTab,
        smartNote,
        setSmartNote,
        getSmartNoteContent,
        settings,
        getNoteContent,
        isSaving,
        setIsSaving,
        isWidget,
        canCreateNote,
        checkNoteCount,
        transcriptTabOpen,
        setTranscriptTabOpen,
        getNotesMetadata,
        showRecordingDialog,
        setShowRecordingDialog,
        currentUsageBytes,
        maxLimitBytes,
        noteCounts,
        fetchNoteCounts,
    };


    return <NotesContext.Provider value={value}>{children}</NotesContext.Provider>
}